import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import TaskDetailsModal from '@/modules/tasks/taskDetails/TaskDetailsModal';
import { getRelationName } from '@/shared/components/RelationName';
import ShareItemModal from '@/shared/components/ShareItemModal';
import YesNoModal from '@/shared/components/YesNoModal';
import { useDataContext } from '@/shared/providers/DataProvider';
import AuthService, { privileges } from '@/shared/services/AuthService';
import { useQueryClient } from '@tanstack/react-query';
import React, { useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SingleRelationMailModal from '@/shared/components/email/SingleRelationMailModal';
import RegistrationModal from '@/shared/components/registration/RegistrationModal';
import CreateOneTimeTask from '@/modules/tasks/CreateOneTimeTask';
import CreateAccessToken from '@/modules/profile/accessTokens/CreateAccessToken';
import CheckboxListModal from '@/shared/components/CheckboxListModal';
import DocumentDetailsModal from '@/modules/documents/details/DocumentDetailsModal';
import { TaskQueryKey } from '../hooks/task/queryKey';
import { getRegistrationModalPropsForTask } from '../components/registration/registrationHelpers';
import { useSendTaskMail } from '../hooks/template/useSendTaskMail';
import { useCreateTaskEmail } from '../hooks/emails/useCreateTaskMail';
import { getShareItemNameForTask } from '../services/ObjectDisplayService';
export var ModalType;
(function (ModalType) {
    ModalType[ModalType["TaskDetail"] = 1] = "TaskDetail";
    ModalType[ModalType["Share"] = 2] = "Share";
    ModalType[ModalType["Delete"] = 3] = "Delete";
    ModalType[ModalType["YesNo"] = 4] = "YesNo";
    ModalType[ModalType["TaskMail"] = 5] = "TaskMail";
    ModalType[ModalType["TimeRegistration"] = 6] = "TimeRegistration";
    ModalType[ModalType["OneTimeTask"] = 7] = "OneTimeTask";
    ModalType[ModalType["CreatePat"] = 8] = "CreatePat";
    ModalType[ModalType["CheckboxList"] = 9] = "CheckboxList";
    ModalType[ModalType["DocumentDetail"] = 10] = "DocumentDetail";
})(ModalType || (ModalType = {}));
export const ModalContext = React.createContext(null);
export const useModalContext = () => {
    const ctx = useContext(ModalContext);
    if (!ctx) {
        throw new Error('Wrap app with ModalProvider');
    }
    return ctx;
};
const ModalProvider = ({ children: childElements }) => {
    const intl = useIntl();
    const [showModalInfo, setShowModalInfo] = useState({});
    const { sendTaskMail } = useSendTaskMail();
    const { createTaskEmail } = useCreateTaskEmail();
    const queryClient = useQueryClient();
    const { users, defaultCompany, userLanguage } = useDataContext();
    const canEditTasks = useMemo(() => AuthService.hasPermission({
        functionality: 'PlannedTask',
        privilege: privileges.Update,
    }), []);
    const canEditDocuments = useMemo(() => AuthService.hasPermission({
        functionality: 'Documents',
        privilege: privileges.Update,
    }), []);
    const { modalType, task, title, message, registration, relationId, documentId, checkboxListItems, showSelectAll, onSave, onClose, onTaskMailModalSaved, } = showModalInfo;
    const value = useMemo(() => ({
        showModalInfo,
        setShowModalInfo,
    }), [showModalInfo, setShowModalInfo]);
    const mapPropertiesAfterStatusChange = (source, target) => {
        if (!source || !target)
            return;
        target.status = source.status;
        target.isBlocked = source.isBlocked;
        target.inapplicable = source.inapplicable;
        target.rejected = source.rejected;
        target.taskColor = source.taskColor;
        target.deadline = source.deadline;
        target.userId = source.userId;
        target.name = source.name;
        target.remark = source.remark;
        target.hasRemark = source.hasRemark;
        target.scheduleHasRemark = source.scheduleHasRemark;
        target.year = source.year;
    };
    const onTaskDetailModalSave = async (updatedTasks) => {
        const previousTasks = queryClient.getQueryData([TaskQueryKey.PlannedTasks]);
        updatedTasks.forEach(newOrUpdatedTask => {
            var _a;
            const updatedTasksQueryData = Object.assign({}, previousTasks);
            const previousMainTasks = (_a = updatedTasksQueryData === null || updatedTasksQueryData === void 0 ? void 0 : updatedTasksQueryData.pages) === null || _a === void 0 ? void 0 : _a.flat(1);
            const isMainTask = !newOrUpdatedTask.parentId;
            const previousMainTask = previousMainTasks.find(x => x.id === (isMainTask ? newOrUpdatedTask.id : newOrUpdatedTask.parentId));
            if (previousMainTask) {
                let { children } = previousMainTask;
                const updatedMainTask = isMainTask ? newOrUpdatedTask : newOrUpdatedTask.parent;
                mapPropertiesAfterStatusChange(updatedMainTask, previousMainTask);
                if (!isMainTask) {
                    const previousSubtask = children.find(x => x.id === newOrUpdatedTask.id);
                    queryClient.invalidateQueries([TaskQueryKey.Remark, newOrUpdatedTask.id]);
                    if (previousSubtask) {
                        mapPropertiesAfterStatusChange(newOrUpdatedTask, previousSubtask);
                    }
                    else {
                        children = [...children, newOrUpdatedTask];
                    }
                    previousMainTask.children = [...children]; // useEffect in KanbanBoard.tsx is not called without this line, and we need it to trigger the reshuffling of columns
                }
                else {
                    queryClient.invalidateQueries([TaskQueryKey.Remark, newOrUpdatedTask.id]);
                    queryClient.invalidateQueries([TaskQueryKey.PlannedTasks]);
                }
            }
            queryClient.setQueryData([TaskQueryKey.PlannedTasks], updatedTasksQueryData);
        });
        setShowModalInfo({});
    };
    const onTaskDetailModalClosed = async () => {
        setShowModalInfo({});
    };
    const onRegistrationCreated = async () => {
        setShowModalInfo({});
    };
    const onCloseTimeRegistration = async () => {
        setShowModalInfo({});
    };
    const onSaveEmail = async (emailViewModel) => {
        const { id: emailId } = await createTaskEmail({
            taskId: task.id,
            relationId: (task.relation && task.relation.id) || task.relationId,
        });
        const sendTaskMailModel = Object.assign(Object.assign({}, emailViewModel), { emailId, language: userLanguage, linkType: emailViewModel.emailLinkType, recipients: emailViewModel.to, templateID: emailViewModel.emailTemplateId });
        setShowModalInfo({});
        await (onSave === null || onSave === void 0 ? void 0 : onSave());
        await sendTaskMail(sendTaskMailModel);
    };
    const onCloseModal = async () => {
        setShowModalInfo({});
        await (onClose === null || onClose === void 0 ? void 0 : onClose());
    };
    const onOneTimeTaskCreated = async () => {
        await queryClient.invalidateQueries([TaskQueryKey.KPI]);
        setShowModalInfo({});
    };
    const onCloseOneTimeTask = async () => {
        setShowModalInfo({});
    };
    const renderModal = () => {
        switch (modalType) {
            case ModalType.TaskDetail: {
                if (!task)
                    return undefined;
                return (_jsx(TaskDetailsModal, { intl: intl, show: true, users: users, task: task, onSave: onTaskDetailModalSave, onClose: onTaskDetailModalClosed, onTaskMailModalSaved: onTaskMailModalSaved, disabled: !canEditTasks }));
            }
            case ModalType.Share:
                if (!task)
                    return undefined;
                return (_jsx(ShareItemModal, { show: true, type: "task", id: task.id, itemName: getShareItemNameForTask(task, intl), relationId: task.relationId, relationName: getRelationName(intl, task.relation), onClose: () => setShowModalInfo({}) }));
            case ModalType.YesNo:
                return (_jsx(YesNoModal, { intl: intl, show: true, title: title, message: message, onNo: onCloseModal, onSave: () => {
                        onSave();
                        setShowModalInfo({});
                    } }));
            case ModalType.TaskMail:
                if (!task)
                    return undefined;
                return (_jsx(SingleRelationMailModal, { intl: intl, show: true, relationId: (task.relation && task.relation.id) || task.relationId, invoiceStreamId: task.invoiceStreamId, companyId: defaultCompany.id, task: task, emailContext: 11, onClose: onCloseModal, onSave: onSaveEmail, type: "task", doNotSendMail: true }));
            case ModalType.TimeRegistration:
                if (!task)
                    return undefined;
                return (_jsx(RegistrationModal, Object.assign({}, getRegistrationModalPropsForTask(task, registration), { show: true, onSave: onRegistrationCreated, onClose: onCloseTimeRegistration, disabled: !canEditTasks })));
            case ModalType.OneTimeTask:
                return (_jsx(CreateOneTimeTask, { show: true, users: users, onSave: onOneTimeTaskCreated, onClose: onCloseOneTimeTask, relationId: relationId }));
            case ModalType.CreatePat:
                return _jsx(CreateAccessToken, { show: true });
            case ModalType.CheckboxList:
                return (_jsx(CheckboxListModal, { title: title, items: checkboxListItems, onClose: onCloseModal, onSave: items => {
                        onSave(items);
                        setShowModalInfo({});
                    }, show: true, sortByLabel: false, showSelectAll: showSelectAll }));
            case ModalType.DocumentDetail:
                return (_jsx(DocumentDetailsModal, { documentId: documentId, show: true, onSave: updatedDocuments => {
                        // onSave(items);
                        setShowModalInfo({});
                    }, onClose: onCloseModal, disabled: !canEditDocuments }));
            default:
                return undefined;
        }
    };
    return (_jsxs(ModalContext.Provider, Object.assign({ value: value }, { children: [renderModal(), childElements] })));
};
export default ModalProvider;
